import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  ArrowDownTrayIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { RotateCcwIcon } from 'components/CustomIcons/RotateCcwIcon';
import { RotateCwIcon } from 'components/CustomIcons/RotateCwIcon';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { Button } from 'components/Form/Button/Button';
import { ButtonGroup } from 'components/Form/Button/ButtonGroup';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

// Required for rendering PDFs that contain JPEG 2000 (JPX) encoded images.
// pdfjs-dist v5 uses a WebAssembly module (OpenJPEG) for JPX decoding, which
// must be explicitly pointed at the wasm directory — otherwise those pages
// render blank.  The wasm/ directory is copied to the build output by the
// viteStaticCopy plugin configured in vite.config.ts.
const PDF_OPTIONS = {
  wasmUrl: '/wasm/',
};

const EXPANDED_PADDING = 48; // px-6 = 24px each side
const COLLAPSED_PADDING = 16; // px-2 = 8px each side

const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;

type Rotation = 0 | 90 | 180 | 270;

type PDFViewerPanelProps = {
  onClose?: () => void;
  onExpand?: () => void;
  isCollapsed?: boolean;
  showDownload?: boolean;
  showClose?: boolean;
  collapsedLabel?: string;
  pdfUrl: string;
  filename: string;
};

export const PDFViewerPanel = ({
  onClose,
  onExpand,
  isCollapsed,
  showDownload = true,
  showClose = true,
  collapsedLabel,
  pdfUrl,
  filename,
}: PDFViewerPanelProps) => {
  const { t } = useTranslation();
  const hasHeader = !isCollapsed || showDownload || showClose;
  const [numPages, setNumPages] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  const [rotation, setRotation] = useState<Rotation>(0);
  const [scale, setScale] = useState<number>(1);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setContainerWidth(width);
        }, 150);
      }
    });

    observer.observe(container);
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    setRotation(0);
    setScale(1);
  }, [pdfUrl]);

  const handleRotateLeft = () => setRotation((r) => ((r - 90 + 360) % 360) as Rotation);
  const handleRotateRight = () => setRotation((r) => ((r + 90) % 360) as Rotation);
  const handleZoomIn = () => setScale((s) => Math.min(+(s + ZOOM_STEP).toFixed(2), ZOOM_MAX));
  const handleZoomOut = () => setScale((s) => Math.max(+(s - ZOOM_STEP).toFixed(2), ZOOM_MIN));

  const padding = isCollapsed ? COLLAPSED_PADDING : EXPANDED_PADDING;
  const baseWidth = containerWidth ? containerWidth - padding : undefined;

  return (
    <div className="flex h-full flex-col bg-gray-50">
      {isCollapsed ? (
        hasHeader && (
          <div className="flex shrink-0 items-center justify-end gap-2 px-2 py-3">
            {showDownload && (
              <Button
                type="button"
                variant="minimal"
                size="extra-small"
                iconOnly
                LeftIcon={ArrowDownTrayIcon}
                analyticsId="transactions:download_original_pdf"
                onClick={() => window.open(pdfUrl, '_blank')}
              >
                {t('common:download')}
              </Button>
            )}
            {showClose && (
              <Button
                type="button"
                variant="minimal"
                size="extra-small"
                iconOnly
                LeftIcon={XMarkIcon}
                analyticsId="transactions:close_pdf_viewer"
                onClick={onClose}
              >
                {t('common:close')}
              </Button>
            )}
          </div>
        )
      ) : (
        <>
          <div className="flex shrink-0 items-center gap-2 bg-white px-6 py-3">
            <h3 className="min-w-0 flex-1 truncate text-sm font-medium text-gray-700">{filename}</h3>
            {showClose && (
              <Button
                type="button"
                variant="minimal"
                size="extra-small"
                iconOnly
                LeftIcon={XMarkIcon}
                analyticsId="transactions:close_pdf_viewer"
                onClick={onClose}
              >
                {t('common:close')}
              </Button>
            )}
          </div>
          <div className="border-t border-gray-200" />
          <div className="flex shrink-0 items-center justify-between px-6 py-2">
            <ButtonGroup>
              <Button
                type="button"
                variant="minimal"
                size="extra-small"
                iconOnly
                LeftIcon={MagnifyingGlassMinusIcon}
                analyticsId="transactions:pdf_zoom_out"
                onClick={handleZoomOut}
                disabled={scale <= ZOOM_MIN}
              >
                {t('common:zoomOut')}
              </Button>
              <span className="w-10 text-center text-sm text-gray-700 tabular-nums">{Math.round(scale * 100)}%</span>
              <Button
                type="button"
                variant="minimal"
                size="extra-small"
                iconOnly
                LeftIcon={MagnifyingGlassPlusIcon}
                analyticsId="transactions:pdf_zoom_in"
                onClick={handleZoomIn}
                disabled={scale >= ZOOM_MAX}
              >
                {t('common:zoomIn')}
              </Button>
            </ButtonGroup>
            <div className="flex items-center gap-2">
              <ButtonGroup>
                <Button
                  type="button"
                  variant="minimal"
                  size="extra-small"
                  iconOnly
                  LeftIcon={RotateCcwIcon}
                  analyticsId="transactions:pdf_rotate_left"
                  onClick={handleRotateLeft}
                >
                  {t('common:rotateLeft')}
                </Button>
                <Button
                  type="button"
                  variant="minimal"
                  size="extra-small"
                  iconOnly
                  LeftIcon={RotateCwIcon}
                  analyticsId="transactions:pdf_rotate_right"
                  onClick={handleRotateRight}
                >
                  {t('common:rotateRight')}
                </Button>
              </ButtonGroup>
              {showDownload && (
                <ButtonGroup>
                  <Button
                    type="button"
                    variant="minimal"
                    size="extra-small"
                    iconOnly
                    LeftIcon={ArrowDownTrayIcon}
                    analyticsId="transactions:download_original_pdf"
                    onClick={() => window.open(pdfUrl, '_blank')}
                  >
                    {t('common:download')}
                  </Button>
                </ButtonGroup>
              )}
            </div>
          </div>
        </>
      )}

      <div className="relative min-h-0 min-w-0 flex-1">
        <div ref={containerRef} className="pointer-events-none absolute inset-0" aria-hidden />
        <div className="absolute inset-0 overflow-auto">
          <Document
            file={pdfUrl}
            options={PDF_OPTIONS}
            loading={null}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          >
            <div className={`flex flex-col ${isCollapsed ? 'items-center gap-2 p-2' : 'items-start gap-4 px-6 pb-4'}`}>
              {Array.from({ length: numPages }, (_, i) => (
                <Page
                  key={i + 1}
                  pageNumber={i + 1}
                  width={baseWidth}
                  scale={isCollapsed ? undefined : scale}
                  rotate={isCollapsed ? undefined : rotation}
                  className={`shadow-lg ${isCollapsed ? 'cursor-pointer overflow-hidden rounded-md ring-2 ring-transparent transition-all hover:ring-gray-400 [&_.annotationLayer]:pointer-events-none [&_.textLayer]:pointer-events-none' : ''}`}
                  onClick={isCollapsed ? onExpand : undefined}
                />
              ))}
              {isCollapsed && collapsedLabel && (
                <span className="text-xs font-medium text-gray-500">{collapsedLabel}</span>
              )}
            </div>
          </Document>
        </div>
      </div>
    </div>
  );
};
